/**
 * Israeli phone normalization, shared by every bulk-import surface.
 *
 * Why this must run on any phone we import: the backend SMS router
 * (services/send-notification/src/domain/smsRouter.ts) chooses a provider by matching the phone
 * against a country rule prefix, normally "+972". A phone that is not in E.164 matches no rule and
 * the message is dropped before a provider is ever called. Nothing surfaces to the seller — the
 * order and the email both succeed — so an unnormalized phone looks like a provider outage.
 *
 * Prod evidence (2026-09-06): two free tickets imported from Excel logged
 *   SMS skipped - no matching countryRule for phone: 0503400233
 * and neither recipient received anything, while ordinary purchases in the same minute sent fine.
 *
 * Extracted from SmsCampaignFromExcelForm, which had carried this logic privately.
 */

/** Digits of an Israeli subscriber number, i.e. the part after the country code. */
const SUBSCRIBER_DIGITS = 9;

/**
 * Normalizes an Israeli phone number to E.164 (+972XXXXXXXXX).
 *
 * Handles the forms people and spreadsheets actually produce:
 *   0544439293      local
 *   544439293       Excel dropped the leading zero from a numeric cell
 *   972544439293    country code without +
 *   +972544439293   already E.164
 *   00972544439293  international dialing prefix
 *
 * Unrecognized input is returned cleaned rather than null, so a caller can show the user what was
 * actually in the cell. Ask {@link isNormalizedIsraeliPhone} whether the result is usable instead
 * of inspecting the string.
 *
 * @param {string|number|null|undefined} phoneNumber raw cell value
 * @returns {string|null} E.164 number, the cleaned input, or null when there was nothing to read
 */
export const normalizeIsraeliPhoneNumber = (phoneNumber) => {
    if (phoneNumber === null || phoneNumber === undefined) {
        return null;
    }

    // Coerce first: a numeric Excel cell arrives as a number and would throw on .trim().
    // The ="..." wrapping comes from "Phone (Excel)" export columns, which quote the value to
    // stop Excel from eating the leading zero.
    const cleaned = phoneNumber
        .toString()
        .trim()
        .replaceAll('-', '')
        .replaceAll(' ', '')
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll('=', '')
        .replaceAll('"', '');

    if (!cleaned.length) {
        return null;
    }

    // Already E.164.
    if (cleaned.startsWith('+972') && cleaned.length === SUBSCRIBER_DIGITS + 4) {
        return cleaned;
    }

    // International dialing prefix.
    if (cleaned.startsWith('00972') && cleaned.length === SUBSCRIBER_DIGITS + 5) {
        return `+972${cleaned.substring(5)}`;
    }

    // Country code without +.
    if (cleaned.startsWith('972') && cleaned.length === SUBSCRIBER_DIGITS + 3) {
        return `+${cleaned}`;
    }

    // Local form with the leading zero.
    if (cleaned.startsWith('0') && cleaned.length === SUBSCRIBER_DIGITS + 1) {
        return `+972${cleaned.substring(1)}`;
    }

    // Bare subscriber number, which is what a numeric Excel cell leaves behind.
    if (cleaned.length === SUBSCRIBER_DIGITS && !cleaned.startsWith('0')) {
        return `+972${cleaned}`;
    }

    // Not a shape we recognize. Hand it back so the caller can report it verbatim.
    return cleaned;
};

/**
 * True when the value is a complete Israeli E.164 number, i.e. one the SMS router can route.
 *
 * @param {string|null|undefined} phone normally the output of {@link normalizeIsraeliPhoneNumber}
 * @returns {boolean}
 */
export const isNormalizedIsraeliPhone = (phone) =>
    typeof phone === 'string' && new RegExp(`^\\+972\\d{${SUBSCRIBER_DIGITS}}$`).test(phone);
